import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../../animations/variants";
import { useSelector } from "react-redux";
import { blogTbcarrer, blogHeaderText } from "i18n/blog";
import {
  User,
  Tag,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  NotebookText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CTAButton from "Util/CTAButton";

const BlogSection = () => {
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();

  const header = blogHeaderText[lang];

  // ✅ sort newest first (important UX)
  const blogs = [...blogTbcarrer].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );

  const formatDate = (dateString, lang) => {
    const date = new Date(dateString);

    const day = date.getDate();

    let month =
      lang === "vi"
        ? `Thg ${date.getMonth() + 1}`
        : date.toLocaleString("en-US", { month: "short" });

    return { day, month };
  };
  const POSTS_PER_PAGE = 3;

  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(blogs.length / POSTS_PER_PAGE);

  const paginatedBlogs = blogs.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE,
  );

  if (!blogs.length) return null;

  return (
    <section className="py-20 bg-darkbg px-6" id="blog">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-widest text-gray-500 mb-3">
            {header.subtitle}
          </p>

          <h2 className="text-3xl md:text-4xl font-bold text-text">
            {header.title}
          </h2>
        </motion.div>

        {/* BLOG GRID */}

        {/* BLOG GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {paginatedBlogs.map((blog) => {
            const t = blog?.[lang];
            if (!t) return null;

            const { day, month } = formatDate(blog.date, lang);

            return (
              <motion.div
                key={blog.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                layout
                className="group bg-navy p-6 rounded-2xl"
                onClick={() => navigate(`/blog/${blog.id}`)}
              >
                {/* IMAGE */}
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src={blog.mainImage}
                    alt={t.title}
                    className={`
              w-full
              h-[280px]
              transition duration-500
              group-hover:scale-105
              ${blog.full ? "object-contain bg-white" : "object-cover"}
            `}
                  />
                </div>

                {/* CARD */}
                <div className="bg-white rounded-2xl shadow-md p-6 -mt-12 mx-4 relative z-10">
                  {/* DATE */}
                  <div className="absolute -top-8 left-6 bg-navy text-white rounded-xl px-4 py-4 text-center shadow-md border-4 border-darkbg">
                    <p className="text-lg font-bold leading-none">{day}</p>

                    <p className="text-xs uppercase tracking-wide opacity-90">
                      {month}
                    </p>
                  </div>

                  {/* META */}
                  <div className="flex justify-end items-center gap-4 text-sm text-gray-500 mb-3 mt-2">
                    <div className="flex items-center gap-1">
                      <User size={14} className="text-neonYellow" />
                      <span>By Admin</span>
                    </div>

                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>

                    <div className="flex items-center gap-1">
                      <Tag size={14} className="text-neonYellow" />
                      <span>{t.section}</span>
                    </div>
                  </div>

                  {/* TITLE */}
                  <h3 className="text-lg font-semibold text-text mb-3 line-clamp-2">
                    {t.title}
                  </h3>

                  {/* READ MORE */}
                  <button
                    onClick={() => navigate(`/blog/${blog.id}`)}
                    className="text-neonYellow font-medium flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    {lang === "vi" ? "Xem thêm →" : "Read More →"}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
        {/* PROFESSIONAL PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-16">
            {/* PREV */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="
        group
        h-12 w-12 rounded-2xl
        border border-white/10
        bg-white/5 backdrop-blur-xl
        flex items-center justify-center
        text-navy/50
        disabled:opacity-30
        hover:bg-neonYellow
        hover:text-navy
        hover:-translate-y-1
        transition-all duration-300
      "
            >
              <ChevronLeft
                size={20}
                className="group-hover:-translate-x-0.5 transition-transform"
              />
            </button>

            {/* PAGE NUMBERS */}
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`
              h-12 w-12 rounded-2xl
              font-semibold text-sm
              transition-all duration-300
              ${
                currentPage === page
                  ? `
                    bg-neonYellow
        text-navy/60
                    shadow-[0_0_25px_rgba(255,214,10,0.35)]
                    scale-110
                  `
                  : `
                    bg-white/5
                    backdrop-blur-xl
                    border border-white/10
        text-navy/60
                    hover:bg-neonYellow
                    hover:text-navy
                  `
              }
            `}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            {/* NEXT */}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="
        group
        h-12 w-12 rounded-2xl
        border border-white/10
        bg-white/5 backdrop-blur-xl
        flex items-center justify-center
        text-navy/50
        disabled:opacity-30
        hover:bg-neonYellow
        hover:text-navy
        hover:-translate-y-1
        transition-all duration-300
      "
            >
              <ChevronRight
                size={20}
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </button>
          </div>
        )}
        <div className="flex justify-center mt-12">
          <CTAButton
            variant="outline"
            icon={NotebookText}
            text={lang === "vi" ? "Xem tất cả" : "View All"}
            onClick={() => navigate("/blogs")}
          />
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
